import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLazyGetContactByIdQuery } from 'store/api/contacts';
import { ExperienceRecordFormData } from 'types/experience-records.ts';
import { getClassifierText } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useClassifiers } from 'utils/useClassifiers';
import { EXPERIENCE_RECORD_FORM_CLASSIFIERS, defaultXRFormValues } from '../experienceRecordFormMapping';

/**
 * The experience record form, for the create and edit pages and the record's cards: its fields, and what it derives
 * from them — the client's business areas and sectors, the cases' team, and the name from the client and the date.
 */
export const useExperienceRecordForm = (initialData?: ExperienceRecordFormData) => {
    const { classifiers, isLoading } = useClassifiers({ codes: EXPERIENCE_RECORD_FORM_CLASSIFIERS });
    const methods = useForm<ExperienceRecordFormData>({
        defaultValues: initialData ?? defaultXRFormValues,
    });
    const { control, setValue } = methods;
    const [confidentialAlertVisible, setConfidentialAlertVisible] = useState(true);

    const [fetchContactDetails] = useLazyGetContactByIdQuery();

    const [watchedClientParty, watchedMatters, watchedDate, watchedIsConfidential] = useWatch({
        control,
        name: ['clientParty', 'matters', 'date', 'isConfidential'],
    });

    // Watch for update connected business areas and connected sectors
    // TODO: cascading-set-state skip — RHF `setValue` calls for two derived business-area/sector fields tied to async client fetch.
    useEffect(() => {
        if (watchedClientParty?.id) {
            fetchContactDetails(watchedClientParty.id.toString())
                .unwrap()
                .then((contactData) => {
                    const businessAreas =
                        contactData.businessAreas
                            ?.map((ba) => getClassifierText(classifiers['BUSINESS_AREA_TYPE'], ba.businessAreaCode))
                            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                            .join(', ') || '';
                    setValue('connectedBusinessAreas', businessAreas);

                    const sectors = contactData.servedBySectorGroups?.map((s) => s.name).join(', ') || '';
                    setValue('connectedSectors', sectors);
                })
                .catch((error) => {
                    console.error('Failed to fetch contact details:', error);
                    logError(error);
                    setValue('connectedBusinessAreas', '');
                    setValue('connectedSectors', '');
                });
        } else {
            setValue('connectedBusinessAreas', '');
            setValue('connectedSectors', '');
        }
    }, [watchedClientParty?.id, fetchContactDetails, setValue, classifiers]);

    // Watch for matters changes and update team fields
    // TODO: cascading-set-state skip — RHF `setValue` calls for multiple derived team fields driven by watched matters.
    useEffect(() => {
        if (watchedMatters && watchedMatters.length > 0) {
            const supervisedByEmployees = [
                ...new Set(
                    watchedMatters.flatMap((matter) =>
                        matter.supervisedByEmployee?.name ? [matter.supervisedByEmployee.name] : []
                    )
                ),
            ];

            const responsibleEmployees = [
                ...new Set(
                    watchedMatters.flatMap((matter) =>
                        matter.responsibleEmployee?.name ? [matter.responsibleEmployee.name] : []
                    )
                ),
            ];

            const handledByEmployees = [
                ...new Set(
                    watchedMatters
                        .flatMap(
                            (matter) =>
                                matter.employees
                                    // Case assistants are STAFF links too; they are not handled-by.
                                    ?.filter((emp) => emp.matterEmployeeRoleTypeCode === 'STAFF' && !emp.administrator)
                                    .map((emp) => emp.employee?.name) || []
                        )
                        .filter(Boolean)
                ),
            ];

            setValue('supervisedBy', supervisedByEmployees.join(', '));
            setValue('responsibleLawyers', responsibleEmployees.join(', '));
            setValue('handledByEmployees', handledByEmployees.join(', '));
        } else {
            setValue('supervisedBy', '');
            setValue('responsibleLawyers', '');
            setValue('handledByEmployees', '');
        }
    }, [watchedMatters, setValue]);

    // Generate XR name when client party or date changes
    useEffect(() => {
        if (watchedClientParty && watchedDate) {
            const clientName = watchedClientParty.name;
            const generatedName = `XR ${clientName} ${watchedDate}`;
            setValue('name', generatedName);
        } else if (!watchedClientParty || !watchedDate) {
            setValue('name', '');
        }
    }, [watchedClientParty, watchedDate, setValue]);

    return {
        methods,
        classifiers,
        isLoading,
        isConfidential: watchedIsConfidential,
        confidentialAlertVisible,
        hideConfidentialAlert: () => setConfidentialAlertVisible(false),
    };
};

export type ExperienceRecordFormState = ReturnType<typeof useExperienceRecordForm>;
